import React, { useState } from 'react';
import {
  ActivityFields_Task_Contact_Fragment,
  Image,
  UpdateTaskMutationVariables,
} from '~/graphql/types';
import BaseEventCard from '../../BaseEventCard';
import asTaskProp from '~/components/page/Tasks/utils/asTaskProp';
import TaskBody from './components/TaskBody';
import BottomBar from './components/BottomBar';
import styled, { css } from 'styled-components';
import { iconForTypeInHeader } from '~/util/taskTypeOptions';
import useConfirmModal from '~/hooks/useConfirmModal';
import { useTimelineContext } from '../../../context';

export type Props = ActivityFields_Task_Contact_Fragment & {
  dataTestId?: string;
  header: {
    text: string;
  };
  icon: Image;
};

const text = {
  title: 'De taak verwijderen',
  message: 'Weet je zeker dat je de taak wilt verwijderen?',
  buttonConfirmTitle: 'Verwijderen',
};

const Task: React.FC<Props> = ({ ...rest }) => {
  const { onUpdateTask, onOpenTaskModal } = useTimelineContext();
  const [loading, setLoading] = useState(false);

  const handleUpdate = async (variables: UpdateTaskMutationVariables) => {
    if (!onUpdateTask) return;
    setLoading(true);
    try {
      await onUpdateTask(variables);
    } finally {
      setLoading(false);
    }
  };

  const { setShowModal, modal } = useConfirmModal({
    level: 'danger',
    labels: {
      title: text.title,
      message: text.message,
    },
    buttons: [
      {
        dataTestId: 'modal-confirm-button',
        label: text.buttonConfirmTitle,
        onClick: async e => {
          e.stopPropagation();
          e.preventDefault();

          await handleUpdate({
            accountId: rest.accountId,
            id: rest.id,
            update: { deleted: true },
          });
        },
      },
    ],
  });

  return (
    <>
      {onUpdateTask && modal}
      <BaseEventCard
        {...rest}
        dataTestId="Task_Card"
        data-objectid={rest.id}
        header={{
          text: rest.header.text,
          icon: iconForTypeInHeader(rest.type),
        }}
        onClick={
          onOpenTaskModal ? () => onOpenTaskModal(asTaskProp(rest)) : undefined
        }
        body={
          <Container>
            <TaskBody {...rest} />
          </Container>
        }
        bottomBar={
          onUpdateTask ? (
            <BottomBar
              onDelete={() => setShowModal(true)}
              loading={loading}
              accountId={rest.accountId}
              id={rest.id}
              onUpdate={handleUpdate}
              status={rest.status}
            />
          ) : null
        }
      />
    </>
  );
};

const Container = styled.div<{}>(
  ({ theme }) => css`
    padding: ${theme.space('base')};
  `,
);

export default Task;
